import { CaretDownOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Dropdown, Space } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { RouteName } from 'config/constants/route.constants';
import { OrderByType } from 'config/constants/search.constants';
import { AuthTranscription, UserTranscription } from 'config/constants/transcription.constant';
import { UserMenuOptions } from 'config/constants/user.constants';
import { MyPurchaseQueryParams } from 'config/types/catalog.type';
import CartUtils from 'config/utils/cart.util';
import NavigationUtils from 'config/utils/navigation.util';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import queryString from 'query-string';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { StorePurchases } from 'store/models/order.model';
import { StoreUser } from 'store/models/user.model';
import { AuthSlice } from 'store/slices/auth.slice';
import { CartSlice } from 'store/slices/cart.slice';
import { OrderSlice } from 'store/slices/order.slice';
import { PurchaseSlice } from 'store/slices/purchase.slice';
import { UserSlice } from 'store/slices/user.slice';

type UserMenuProps = {
  user: StoreUser;
  purchase: StorePurchases;
};

const UserMenu: React.FC<UserMenuProps> = ({ user: { userProfile }, purchase }) => {
  const { page = 1, orderBy = OrderByType.DESC } = purchase;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleMyPurchaseOption = (page: number, orderBy: OrderByType) => {
    const myPurchaseParams: MyPurchaseQueryParams = { page, orderBy };
    const purchaseQueryString = queryString.stringify(myPurchaseParams, NavigationUtils.queryStringOptions);
    dispatch(PurchaseSlice.refreshOrderBy(orderBy));
    dispatch(PurchaseSlice.refreshPageNumber(page));
    navigate(`${RouteName.MY_PURCHASES}?${purchaseQueryString}`, NavigationUtils.navigateOptions);
  };

  const handleLogout = async () => {
    try {
      dispatch(AuthSlice.fetchingRefresh(true));
      await CartUtils.persistCartBeforeLogout();
    } catch (e_) {
      SnackbarUtilities.error(AuthTranscription.ERROR_LOGOUT);
    } finally {
      CartUtils.resetCartSession();
      dispatch(AuthSlice.resetAuhStore());
      dispatch(UserSlice.resetUserStore());
      dispatch(CartSlice.resetCartStore());
      dispatch(OrderSlice.resetOrderStore());
      dispatch(PurchaseSlice.resetPurchaseStore());
      AuthIntentUtils.clearIntent();
      dispatch(AuthSlice.fetchingRefresh(false));
    }
  };

  const handleMenuClick = (key: string, _page: number, _orderBy: OrderByType): void => {
    switch (key) {
      case UserMenuOptions.PROFILE:
        navigate(`${RouteName.MY_PROFILE}?tab=${NavigationUtils.AddressUrlTab.MY_INFO}`, NavigationUtils.navigateOptions);
        break;
      case UserMenuOptions.PURCHASES:
        handleMyPurchaseOption(_page, _orderBy);
        break;
      case UserMenuOptions.FAVORITES:
        navigate(RouteName.MY_FAVORITES, NavigationUtils.navigateOptions);
        break;
      case UserMenuOptions.LOGOUT:
        handleLogout();
        break;
    }
  };

  const handleMenuItems = (page: number, orderBy: OrderByType): ItemType[] => {
    return [
      {
        key: UserMenuOptions.PROFILE,
        label: UserTranscription.USER_MY_PROFILE,
        onClick: ({ key }) => handleMenuClick(key, page, orderBy),
      },
      {
        key: UserMenuOptions.PURCHASES,
        label: UserTranscription.USER_MY_PURCHASES,
        onClick: ({ key }) => handleMenuClick(key, page, orderBy),
      },
      {
        key: UserMenuOptions.FAVORITES,
        label: UserTranscription.USER_MY_FAVORITES,
        onClick: ({ key }) => handleMenuClick(key, page, orderBy),
      },
      {
        type: 'divider',
      },
      {
        key: UserMenuOptions.LOGOUT,
        label: AuthTranscription.LOGOUT,
        icon: <LogoutOutlined />,
        danger: true,
        onClick: ({ key }) => handleMenuClick(key, page, orderBy),
      },
    ];
  };

  return (
    <React.Fragment>
      <Dropdown menu={{ items: handleMenuItems(page, orderBy) }}>
        <a onClick={(e) => e.preventDefault()}>
          <Space>
            <UserOutlined />
            {userProfile.name ? `${userProfile.name} ${userProfile.lastName}` : userProfile.email}
            <CaretDownOutlined />
          </Space>
        </a>
      </Dropdown>
    </React.Fragment>
  );
};
export default UserMenu;
